import { useEffect, useState } from 'react'
import {
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Text,
  Textarea
} from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { primary } from 'theme/globalStyles'
import { createFeedback } from 'API/feedback'

const experienceSurvey =
  'https://docs.google.com/forms/d/e/1FAIpQLSeWoN6VyeXAWYa-9m3fV39VIFQb6qzZQUxcZDPFKi8QAy8okQ/viewform'

interface IFeedbackModalProps {
  isOpen: boolean
  onClose: () => void
}

const FeedbackModal = (props: IFeedbackModalProps) => {
  const { isOpen, onClose } = props
  const { authStore, organizationStore } = useStores()
  const { userDetail } = authStore
  const { organization } = organizationStore
  const currentTheme = organization?.theme ?? {}
  const methods = useForm()
  const { getValues, handleSubmit, register, reset } = methods
  const [isLoading, setIsLoading] = useState(false)

  async function onSubmit(): Promise<void> {
    try {
      setIsLoading(true)
      const text: string = getValues('text')
      if (text) {
        await createFeedback({ userId: userDetail?.id, text })
        onClose()
      }
      authStore.setShowSurveyPopup(false)
      setIsLoading(false)
      toast.success('Sent successfully')
    } catch (error: any) {
      setIsLoading(false)
      toast.error('Sent failed')
    }
  }

  useEffect(() => {
    if (isOpen) {
      reset()
    }
  }, [isOpen])

  return (
    <>
      <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalOverlay />
            <ModalContent maxWidth="610px">
              <ModalHeader color="gray.800" fontSize="18px" fontWeight={500} lineHeight={7}>
                Give feedback
              </ModalHeader>
              <ModalCloseButton background="white" border="none" boxShadow="none !important" />
              <ModalBody borderY="1px solid #E2E8F0" padding={6}>
                <Text color="gray.700" fontSize="16px" fontWeight={400} lineHeight={6} marginBottom={6}>
                  By sharing your thoughts, you will help us understand your needs and feelings
                </Text>
                <FormControl>
                  <FormLabel marginBottom={2} color="gray.700">
                    Description
                  </FormLabel>
                  <Textarea
                    {...register('text')}
                    isRequired
                    placeholder="Share your thoughts, suggestions, or concerns to help us improve"
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <HStack width="full" justify="space-between">
                  <Text
                    color="primary.500"
                    fontSize="16px"
                    fontWeight={500}
                    lineHeight={6}
                    cursor="pointer"
                    onClick={() => window.open(experienceSurvey, '_blank')}
                  >
                    Take the Experience Survey
                  </Text>
                  <HStack spacing={4}>
                    <Button
                      color="gray.700"
                      background="white"
                      fontSize="16px"
                      fontWeight={500}
                      lineHeight={6}
                      border="1px solid #E2E8F0"
                      isLoading={isLoading}
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      border="0"
                      color="white"
                      fontSize="16px"
                      fontWeight={500}
                      lineHeight={6}
                      isLoading={isLoading}
                      background={currentTheme?.primaryColor ?? primary}
                      _hover={{ opacity: 0.8 }}
                    >
                      Send
                    </Button>
                  </HStack>
                </HStack>
              </ModalFooter>
            </ModalContent>
          </form>
        </FormProvider>
      </Modal>
    </>
  )
}

export default observer(FeedbackModal)
